import { useState, useEffect, useCallback, useMemo } from 'react';
import { toast } from 'sonner';
import { FileSpreadsheet, RefreshCw, Download, Search, Loader2 } from 'lucide-react';
import { GlassCard } from '@/components/custom/GlassCard';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { SortableTh, sortRows, dateValue, type TableSort, type SortKind } from '@/components/ui/sortable-table';
import { apiFetch, apiFetchBlob } from '@/lib/api-client';
import { JvidLink } from '@/components/dmp/DmpLookup';

// Admin-only « Registre JVID » (user ask 2026-07-29) — the audit trail of every JVID the app minted:
// what was created, from which ticket, by whom, and what happened on the SharePoint side (pushed to
// which worksheet / skipped because already in a Build PST / still pending, with the error). The
// LOCAL table is the source of truth — a row survives even if someone edits the Excel by hand.
// French-only labels, like the Action-log admin page.

interface JvidRegistryRow {
  id: string;
  jvid: number;
  action: string;
  streetName: string | null;
  houseNumber: string | null;
  houseNumberExtension: string | null;
  busNumber: string | null;
  postalCode: string | null;
  municipality: string | null;
  city: string | null;
  dwellingUnitType: string | null;
  ticketNumber: string | null;
  actorEmail: string | null;
  target: string;
  addressStatus: string | null;
  createdAt: string;
  syncedToSharepointAt: string | null;
  syncError: string | null;
  pstFile: string | null;
  pushedTab: string | null;
  /** N29.1 — la réponse à « Build doit-il avoir cette adresse ? ». null = créée avant la fenêtre (règle du 29-07). */
  pstBis: boolean | null;
  pstBisDecidedAt: string | null;
  pstBisDecidedBy: string | null;
}

// N29.1 (plan validé le 24-09) — la décision PST bis, en clair. « Non (pas proposée) » : la fenêtre ne s'est pas
// ouverte, Build n'ayant pas répondu sur le ticket ; « — » : ligne d'avant la fenêtre.
function pstBisLabel(r: JvidRegistryRow): string {
  if (r.pstBis == null) return '—';
  if (r.pstBis) return 'Oui';
  return r.pstBisDecidedBy ? 'Non' : 'Non (pas proposée)';
}

/** Seul un « Oui » part vers le PST bis : tout le reste, non poussé, n'est plus « en attente » — il ne partira pas. */
const willNeverPush = (r: JvidRegistryRow) => !r.pstFile && !r.syncedToSharepointAt && r.pstBis !== true;

/** Pourquoi la ligne porte cette décision — la bulle de la colonne et du badge. « Pas proposée » a deux causes qu'il
 *  ne faut pas confondre (vu à l'écran le 25-09) : le passage en MDU n'a aucun écran, donc jamais de question ; et une
 *  création sans réponse de Build sur un ticket (ou sans ticket du tout, file MDU) ne la voit pas s'ouvrir. */
function pstBisWhy(r: JvidRegistryRow): string {
  if (r.pstBis == null) return 'Créée avant la fenêtre PST bis (envoi d\'office, règle du 29-07)';
  if (r.pstBisDecidedBy) return `PST bis : « ${r.pstBis ? 'Oui' : 'Non'} », répondu par ${r.pstBisDecidedBy} le ${fmt(r.pstBisDecidedAt)}`;
  if (r.action === 'CONVERT_TO_MDU') return 'Passage en MDU : la question n\'est jamais posée (aucun écran) — « Non » par défaut';
  return 'Question PST bis non posée : pas de réponse de Build sur un ticket — « Non » par défaut';
}

const ACTION_LABEL: Record<string, string> = {
  CREATE: 'Création',
  ADD_BOX: 'Ajout boîte',
  CONVERT_TO_MDU: 'Conversion MDU',
};

function fmt(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString('fr-BE', { dateStyle: 'short', timeStyle: 'short' }) : '—';
}

// Colonnes triables — libellé, type de tri, et la valeur lue par le comparateur, déclarés ensemble
// pour qu'un en-tête ne puisse pas être cliquable sans que le tri suive.
const COLUMNS: { key: string; label: string; kind: SortKind; value: (r: JvidRegistryRow) => string | number | null }[] = [
  { key: 'jvid', label: 'JVID', kind: 'number', value: (r) => r.jvid },
  { key: 'action', label: 'Action', kind: 'text', value: (r) => ACTION_LABEL[r.action] ?? r.action },
  { key: 'address', label: 'Adresse', kind: 'text', value: (r) => [r.streetName, r.houseNumberExtension || r.houseNumber].filter(Boolean).join(' ') || null },
  { key: 'bus', label: 'Boîte', kind: 'text', value: (r) => r.busNumber },
  { key: 'city', label: 'Ville', kind: 'text', value: (r) => r.city || r.municipality },
  { key: 'ticket', label: 'Ticket', kind: 'text', value: (r) => r.ticketNumber },
  { key: 'createdAt', label: 'Créé le', kind: 'date', value: (r) => dateValue(r.createdAt) },
  // Trier « Push SharePoint » sur l'ÉTAT lisible (le badge), pas sur une date interne : c'est ce que
  // l'admin lit dans la colonne, et ça regroupe les « Retenu » à trancher.
  { key: 'push', label: 'Push SharePoint', kind: 'text', value: (r) => (r.pstFile ? '1-Déjà en PST' : r.syncedToSharepointAt ? '2-Poussé' : willNeverPush(r) ? '5-Non envoyée' : r.syncError ? '3-Retenu' : '4-En attente') },
  { key: 'pstBis', label: 'PST bis', kind: 'text', value: (r) => (r.pstBis == null ? null : pstBisLabel(r)) },
  { key: 'actor', label: 'Par', kind: 'text', value: (r) => r.actorEmail },
];
const COLUMN_VALUE = new Map(COLUMNS.map((c) => [c.key, c.value]));

// Push outcome, one glance: pushed (where/when) / already in a PST (no line, by design) / pending.
function PushBadge({ r }: { r: JvidRegistryRow }) {
  if (r.pstFile) {
    return (
      <span
        className="inline-flex items-center text-[10px] font-bold uppercase bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded"
        title={`Déjà présent dans le PST Build : ${r.pstFile.split('/').pop()} — aucune ligne Excel (comportement voulu)`}
      >
        Déjà en PST
      </span>
    );
  }
  if (r.syncedToSharepointAt) {
    return (
      <span
        className="inline-flex items-center text-[10px] font-bold uppercase bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded"
        title={`Poussé le ${fmt(r.syncedToSharepointAt)}${r.pushedTab ? ` → onglet « ${r.pushedTab} »` : ''}`}
      >
        Poussé
      </span>
    );
  }
  // N29.1 : sans « Oui », la ligne ne part plus — l'afficher « En attente » promettrait un envoi qui n'aura pas lieu.
  if (willNeverPush(r)) {
    return (
      <span
        className="inline-flex items-center text-[10px] font-bold uppercase bg-slate-100 text-slate-400 px-1.5 py-0.5 rounded"
        title={r.pstBis == null
          ? `${pstBisWhy(r)} : ne part plus. À arbitrer dans l'audit du PST bis.`
          : `${pstBisWhy(r)} — rien ne part.`}
      >
        Non envoyée
      </span>
    );
  }
  return (
    <span
      className={
        r.syncError
          ? 'inline-flex items-center text-[10px] font-bold uppercase bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded'
          : 'inline-flex items-center text-[10px] font-bold uppercase bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded'
      }
      title={r.syncError ?? 'Pas encore poussé — le prochain cycle le prendra'}
    >
      {r.syncError ? 'Retenu' : 'En attente'}
    </span>
  );
}

export function JvidRegistry() {
  const [rows, setRows] = useState<JvidRegistryRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [search, setSearch] = useState('');
  // Mêmes filtres que la table de mapping (Anouk 2026-08-04) : par créateur et par date, côté client.
  const [byActor, setByActor] = useState('');
  const [byAction, setByAction] = useState('');
  const [fromDay, setFromDay] = useState('');
  const [toDay, setToDay] = useState('');
  const [sort, setSort] = useState<TableSort | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiFetch<{ data: JvidRegistryRow[] }>('/admin/jvid-registry?limit=2000');
      setRows(res.data);
    } catch (err: any) {
      toast.error(err?.message || 'Chargement du registre impossible');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const actors = useMemo(
    () => [...new Set(rows.map((r) => r.actorEmail).filter((v): v is string => !!v))].sort((a, b) => a.localeCompare(b, 'fr')),
    [rows],
  );
  const actions = useMemo(
    () => [...new Set(rows.map((r) => r.action))].sort((a, b) => a.localeCompare(b, 'fr')),
    [rows],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const kept = rows.filter((r) => {
      if (byActor && r.actorEmail !== byActor) return false;
      if (byAction && r.action !== byAction) return false;
      // Bornes inclusives, comparées en jours calendaires : choisir le même jour des deux côtés
      // doit rendre toute cette journée.
      const day = r.createdAt.slice(0, 10);
      if (fromDay && day < fromDay) return false;
      if (toDay && day > toDay) return false;
      if (!q) return true;
      return String(r.jvid).includes(q)
        || (r.city ?? '').toLowerCase().includes(q)
        || (r.streetName ?? '').toLowerCase().includes(q)
        || (r.ticketNumber ?? '').toLowerCase().includes(q)
        || (r.pushedTab ?? '').toLowerCase().includes(q);
    });
    return sortRows(kept, sort, (r, key) => COLUMN_VALUE.get(key)?.(r) ?? null);
  }, [rows, search, byActor, byAction, fromDay, toDay, sort]);

  const filtersOn = byActor !== '' || byAction !== '' || fromDay !== '' || toDay !== '';

  async function handleExport() {
    setExporting(true);
    try {
      const blob = await apiFetchBlob('/admin/jvid-registry/export.csv');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'jvid-registry.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err: any) {
      toast.error(err?.message || 'Export CSV impossible');
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-brand-gradient flex items-center justify-center">
          <FileSpreadsheet className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-800">Registre JVID</h2>
          <p className="text-sm text-slate-500">
            Historique de tous les JVID créés par l'outil et de leur envoi vers « Extra JVID Build.xlsx » ({rows.length} lignes)
          </p>
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
            Rafraîchir
          </Button>
          <Button size="sm" onClick={handleExport} disabled={exporting}>
            {exporting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Download className="w-4 h-4 mr-1.5" />}
            Export CSV
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative max-w-md flex-1 min-w-[16rem]">
          <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="JVID, ville, rue, ticket, onglet…"
            className="pl-9"
            aria-label="Recherche dans le registre"
          />
        </div>
        <select
          value={byActor}
          onChange={(e) => setByActor(e.target.value)}
          aria-label="Filtrer par créateur"
          className="h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-700 focus:outline-none focus:border-brand-rose/40"
        >
          <option value="">Créé par…</option>
          {actors.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
        <select
          value={byAction}
          onChange={(e) => setByAction(e.target.value)}
          aria-label="Filtrer par action"
          className="h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-700 focus:outline-none focus:border-brand-rose/40"
        >
          <option value="">Action…</option>
          {actions.map((a) => <option key={a} value={a}>{ACTION_LABEL[a] ?? a}</option>)}
        </select>
        <input
          type="date"
          value={fromDay}
          onChange={(e) => setFromDay(e.target.value)}
          aria-label="Créé à partir du"
          title="Créé à partir du"
          className="h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-700 focus:outline-none focus:border-brand-rose/40"
        />
        <input
          type="date"
          value={toDay}
          onChange={(e) => setToDay(e.target.value)}
          aria-label="Créé jusqu'au"
          title="Créé jusqu'au"
          className="h-9 rounded-lg border border-slate-200 bg-white px-2.5 text-sm text-slate-700 focus:outline-none focus:border-brand-rose/40"
        />
        {filtersOn && (
          <button
            type="button"
            onClick={() => { setByActor(''); setByAction(''); setFromDay(''); setToDay(''); }}
            className="h-9 rounded-lg px-2.5 text-sm font-semibold text-brand-rose hover:underline"
          >
            Effacer les filtres
          </button>
        )}
        {(filtersOn || search.trim() !== '') && (
          <span className="text-xs text-slate-400">{filtered.length} sur {rows.length} lignes</span>
        )}
      </div>

      {loading ? (
        <div className="space-y-2">{[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white py-16 text-center text-slate-400">
          Aucune ligne
        </div>
      ) : (
        <GlassCard className="overflow-hidden p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 text-left text-xs uppercase tracking-wide">
                {COLUMNS.map((c) => (
                  <SortableTh
                    key={c.key}
                    label={c.label}
                    sortKey={c.key}
                    kind={c.kind}
                    sort={sort}
                    onSort={setSort}
                    title="Trier sur cette colonne"
                    className="px-4"
                  />
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-t border-slate-100">
                  <td className="px-4 py-3 font-medium text-slate-800"><JvidLink jvid={r.jvid} /></td>
                  <td className="px-4 py-3 text-slate-600">{ACTION_LABEL[r.action] ?? r.action}</td>
                  <td className="px-4 py-3 text-slate-600">
                    {[r.streetName, r.houseNumberExtension || r.houseNumber].filter(Boolean).join(' ') || '—'}
                  </td>
                  <td className="px-4 py-3 text-slate-600">{r.busNumber || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{r.city || r.municipality || '—'}</td>
                  <td className="px-4 py-3 text-slate-600">{r.ticketNumber ?? '—'}</td>
                  <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmt(r.createdAt)}</td>
                  <td className="px-4 py-3">
                    <PushBadge r={r} />
                    {r.syncedToSharepointAt && !r.pstFile && (
                      <span className="ml-2 text-xs text-slate-500 whitespace-nowrap">
                        {fmt(r.syncedToSharepointAt)}{r.pushedTab ? ` · ${r.pushedTab}` : ''}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={
                        r.pstBis
                          ? 'inline-flex items-center text-[10px] font-bold uppercase bg-sky-100 text-sky-700 px-1.5 py-0.5 rounded'
                          : 'text-xs text-slate-500'
                      }
                      title={pstBisWhy(r)}
                    >
                      {pstBisLabel(r)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{r.actorEmail ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </GlassCard>
      )}
    </div>
  );
}
